import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import io, { Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

// For local dev: React runs on 3001, backend on 5001
// Socket.IO needs the direct backend URL (proxy doesn't work for WebSocket)
const SOCKET_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// --- TYPE DEFINITIONS ---
interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

// --- CUSTOM HOOK ---
export function useSocket(): SocketContextType {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}

// --- PROVIDER ---
interface SocketProviderProps {
  children: ReactNode;
}

export function SocketProvider({ children }: SocketProviderProps) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const { currentUser } = useAuth();

  // Connect socket with authentication
  const connectSocket = useCallback(async () => {
    if (currentUser && !socket) {
      try {
        console.log('🔑 User detected, attempting to connect socket...');
        const token = await currentUser.getIdToken();

        const newSocket: Socket = io(SOCKET_URL, {
          transports: ['polling'],
          auth: { token },
        });

        newSocket.on('connect', () => {
          console.log('✅ Socket connected successfully with authentication!');
          setSocket(newSocket);
        });

        newSocket.on('disconnect', () => {
          console.log('❌ Socket disconnected.');
          setSocket(null);
        });

        newSocket.on('connect_error', (err) => {
          console.error(`🚨 Socket connection error: ${err.message}`);
        });
      } catch (error) {
        console.error('Could not get token for socket connection.', error);
      }
    }
  }, [currentUser, socket]);

  // Disconnect socket
  const disconnectSocket = useCallback(() => {
    if (socket) {
      console.log('🔌 Disconnecting socket...');
      socket.disconnect();
      setSocket(null);
    }
  }, [socket]);

  // Manage socket lifecycle based on auth state
  useEffect(() => {
    if (currentUser) {
      connectSocket();
    } else {
      disconnectSocket();
    }

    return () => {
      disconnectSocket();
    };
  }, [currentUser, connectSocket, disconnectSocket]);

  const value: SocketContextType = {
    socket,
    isConnected: socket?.connected || false,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

